import * as React from 'react';
import styled from 'styled-components';
import { Box, Grid, GridCell, Text } from '@urbaninfrastructure/react-ui-kit';
import { useIntl } from 'react-intl';

const TariffCard = styled(Box)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  background: white;
  overflow: hidden;
`;

const BikeImage = styled.img`
  max-width: 200px;
  height: 150px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
`;

const FeeRow = styled(Box)`
  padding: 4px 0;
`;

type BikeType = {
  title: string;
  subtitle: string;
  image: string;
  maxDuration: string;
  freeSwitch: string;
  fees: string[];
  penalty: string;
};

const bikesByLocale: Record<string, BikeType[]> = {
  it: [
    {
      title: 'LA GIALLA',
      subtitle: 'BICICLETTA TRADIZIONALE',
      image: '/static/milan-bikemi/Yellow bikeMi.png',
      maxDuration:
        'Puoi pedalare sulla stessa bici fino ad un max di 2 ore consecutive',
      freeSwitch:
        'Al prelievo della tua bikeMi: primi 3 minuti gratuiti per cambio bici immediato',
      fees: [
        'Da 3 a 30 minuti: gratuiti',
        'Da 30 minuti a 2 ore: 0,50 € ogni 30 minuti'
      ],
      penalty: 'Oltre le 2 ore: penalità! 2 € per ogni ora di ritardo'
    },
    {
      title: 'LA ROSSA',
      subtitle: 'ELETTRICA',
      image: '/static/milan-bikemi/Red e-bikeMi.png',
      maxDuration:
        'Puoi pedalare sulla stessa bici fino ad un max di 2 ore consecutive',
      freeSwitch:
        'Al prelievo della tua bikeMi: primi 3 minuti gratuiti per cambio bici immediato',
      fees: [
        'Da 3 a 30 minuti: 0,25 €',
        'Da 30 minuti a 60 minuti: 0,50 € (totale 0,75 €)',
        'Da 60 minuti a 90 minuti: 1 € (totale 1,75 €)',
        'Da 90 minuti a 2 ore: 2 € (totale 3,75 €)'
      ],
      penalty: 'Oltre le 2 ore: penalità! 4 € per ogni ora di ritardo'
    },
    {
      title: 'LA ROSSA',
      subtitle: 'ELETTRICA CON SEGGIOLINO',
      image: '/static/milan-bikemi/Red CS e-bikeMi.png',
      maxDuration:
        'Puoi pedalare sulla stessa bici fino ad un max di 2 ore consecutive',
      freeSwitch:
        'Al prelievo della tua bikeMi: primi 3 minuti gratuiti per cambio bici immediato',
      fees: [
        'Da 3 a 30 minuti: 0,25 €',
        'Da 30 minuti a 60 minuti: 0,50 € (totale 0,75 €)',
        'Da 60 minuti a 90 minuti: 1 € (totale 1,75 €)',
        'Da 90 minuti a 2 ore: 2 € (totale 3,75 €)'
      ],
      penalty: 'Oltre le 2 ore: penalità! 4 € per ogni ora di ritardo'
    }
  ],
  en: [
    {
      title: 'LA GIALLA',
      subtitle: 'REGULAR BIKE',
      image: '/static/milan-bikemi/Yellow bikeMi.png',
      maxDuration: 'You can ride your bike for up to 2 hours on end',
      freeSwitch:
        'When you pick up your bikeMi, the first 3 minutes are free if you change tyour bike right then',
      fees: [
        '3 to 30 minutes: free',
        '30 minutes to 2 hours: €0.50 per 30 minutes'
      ],
      penalty: 'Over 2 hours: €2.00 penalty for each hour of delay'
    },
    {
      title: 'LA ROSSA',
      subtitle: 'ELECTRIC BIKE',
      image: '/static/milan-bikemi/Red e-bikeMi.png',
      maxDuration: 'You can ride your bike for up to 2 hours on end',
      freeSwitch:
        'When you pick up your bikeMi, the first 3 minutes are free if you change your bike right then',
      fees: [
        '3 to 30 minutes: €0.25',
        '30 to 60 minutes: €0.50 (total €0.75)',
        '60 minutes to 90 minutes: €1.00 (total €1.75)',
        '90 minutes to 2 hours: €2.00 (total €3.75)'
      ],
      penalty: 'Over 2 hours: €4.00 for each hour of delay'
    },
    {
      title: 'LA ROSSA',
      subtitle: 'ELECTRIC BIKE WITH CHILD SEAT',
      image: '/static/milan-bikemi/Red CS e-bikeMi.png',
      maxDuration: 'You can ride your bike for up to 2 hours on end',
      freeSwitch:
        'When you pick up your bikeMi, the first 3 minutes are free if you change your bike right then',
      fees: [
        '3 to 30 minutes: €0.25',
        '30 to 60 minutes: €0.50 (total €0.75)',
        '60 minutes to 90 minutes: €1.00 (total €1.75)',
        '90 minutes to 2 hours: €2.00 (total €3.75)'
      ],
      penalty: 'Over 2 hours: €4.00 penalty for each hour of delay'
    }
  ]
};

export const BikeMiTariffs: React.FC = () => {
  const intl = useIntl();
  const bikes = bikesByLocale[intl.locale] || bikesByLocale.en;

  return (
    <Grid justifyContent="center" alignItems="stretch">
      {bikes.map((bike, index) => (
        <GridCell key={index} width={{ _: 1, md: 1 / 2, xl: 1 / 3 }} mt="xxs">
          <TariffCard p={{ _: 3, md: 4 }}>
            <BikeImage
              src={bike.image}
              alt={`${bike.title} – ${bike.subtitle}`}
            />

            <Box mt={3} mb={2}>
              <Text typoStyle="sm" bold>
                {bike.title}
              </Text>
              <Text typoStyle="xxs" bold>
                {bike.subtitle}
              </Text>
            </Box>

            <Box mb={2}>
              <Text typoStyle="xxs" style={{ fontStyle: 'italic' }}>
                {bike.maxDuration}
              </Text>
            </Box>

            <Box mb={2}>
              <Text typoStyle="xxs" bold>
                {bike.freeSwitch}
              </Text>
            </Box>

            {bike.fees.map((fee, i) => (
              <FeeRow key={i}>
                <Text typoStyle="xxs">{fee}</Text>
              </FeeRow>
            ))}

            <Box mt={2}>
              <Text typoStyle="xxs" bold color="red">
                {bike.penalty}
              </Text>
            </Box>
          </TariffCard>
        </GridCell>
      ))}
    </Grid>
  );
};
